import { useLazyUpdateAppPermissionQuery } from "../features/Feature_GeneralAppElements/GeneralAppElements_Api";
import { setAppPermission } from "../features/Feature_GeneralAppElements/GeneralAppElements_Slice";
import { T_Update_App_Permission_Request } from "../features/Feature_GeneralAppElements/GeneralAppElements_Types";
import {
  useAppDispatch,
  useAppSelector,
} from "../redux-store/CustomReduxHooks";
import {
  createUpdatePermissionData,
  getMobileOperatingSystem,
} from "../utils/helper";
import {
  nativeCallBackMethodForCamera,
  nativeCallBackMethodForLocation,
  nativeCallBackMethodForMicrophone,
  nativeCallBackMethodForNotification,
  nativeCallBackMethodForStorage,
} from "../utils/NativeAppEvents";
import { API_STATUS_CODE } from "../utils/ApiEndpoints";

const useNativePermissionSync = () => {
  const [updateAppPermission] = useLazyUpdateAppPermissionQuery();
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const appDispatch = useAppDispatch();

  const callUpdateApi = async (
    mobileNo: string,
    csUserId: string,
    permissionUpdates: Partial<T_Update_App_Permission_Request["permissions"]>,
  ) => {
    const payload: T_Update_App_Permission_Request = {
      mobileNumber: mobileNo,
      csUserId: csUserId,
      permissions:
        permissionUpdates as T_Update_App_Permission_Request["permissions"],
    };
    const response = await updateAppPermission(payload);
    if (response.data?.code === API_STATUS_CODE.SUCCESS) {
      const updatePermissionData = createUpdatePermissionData(
        response.data.payload,
      );
      appDispatch(setAppPermission(updatePermissionData));
    }
  };

  const syncAndroidPermissions = (mobileNo: string, csUserId: string) => {
    if (!window.ReactNativeWebView) return;

    const collected: Record<string, boolean> = {};
    const expected = [
      "camera",
      "notifications",
      "location",
      "storage",
      "microphone",
    ];
    const received = new Set<string>();

    const tryFlush = async () => {
      if (received.size === expected.length) {
        await callUpdateApi(mobileNo, csUserId, {
          camera: collected["camera"] ?? false,
          notifications: collected["notifications"] ?? false,
          location: collected["location"] ?? false,
          storage: collected["storage"] ?? false,
          microphone: collected["microphone"] ?? false,
        });
        cleanup();
      }
    };

    const cameraHandler = (e: CustomEvent) => {
      collected["camera"] = e.detail === "true";
      received.add("camera");
      tryFlush();
    };
    const notificationHandler = (e: CustomEvent) => {
      collected["notifications"] = e.detail === "true";
      received.add("notifications");
      tryFlush();
    };
    const locationHandler = (e: CustomEvent) => {
      collected["location"] = e.detail === "true";
      received.add("location");
      tryFlush();
    };
    const storageHandler = (e: CustomEvent) => {
      collected["storage"] = e.detail === "true";
      received.add("storage");
      tryFlush();
    };
    const microphoneHandler = (e: CustomEvent) => {
      collected["microphone"] = e.detail === "true";
      received.add("microphone");
      tryFlush();
    };

    const cleanup = () => {
      window.removeEventListener(
        "android_camera_permission",
        cameraHandler as EventListener,
      );
      window.removeEventListener(
        "android_notification_permission",
        notificationHandler as EventListener,
      );
      window.removeEventListener(
        "android_location_permission",
        locationHandler as EventListener,
      );
      window.removeEventListener(
        "android_storage_permission",
        storageHandler as EventListener,
      );
      window.removeEventListener(
        "android_microphone_permission",
        microphoneHandler as EventListener,
      );
    };

    window.addEventListener(
      "android_camera_permission",
      cameraHandler as EventListener,
    );
    window.addEventListener(
      "android_notification_permission",
      notificationHandler as EventListener,
    );
    window.addEventListener(
      "android_location_permission",
      locationHandler as EventListener,
    );
    window.addEventListener(
      "android_storage_permission",
      storageHandler as EventListener,
    );
    window.addEventListener(
      "android_microphone_permission",
      microphoneHandler as EventListener,
    );

    window.ReactNativeWebView.postMessage(
      JSON.stringify({ action: "REQUEST_CAMERA_PERMISSION" }),
    );
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ action: "REQUEST_NOTIFICATION_PERMISSION" }),
    );
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ action: "REQUEST_LOCATION_PERMISSION" }),
    );
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ action: "REQUEST_STORAGE_PERMISSION" }),
    );
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ action: "REQUEST_MICROPHONE_PERMISSION" }),
    );

    // Safety cleanup after 5s if native never responds
    setTimeout(cleanup, 5000);
  };

  const syncIosPermissions = async (mobileNo: string, csUserId: string) => {
    const results = await Promise.allSettled([
      nativeCallBackMethodForCamera({
        type: "getCameraPermissionInfo",
        isGrant: "false",
      }),
      nativeCallBackMethodForLocation({
        type: "getLocationPermissionInfo",
        isGrant: "false",
      }),
      nativeCallBackMethodForStorage({
        type: "getStoragePermissionInfo",
        isGrant: "false",
      }),
      nativeCallBackMethodForMicrophone({
        type: "getMicrophonePermissionInfo",
        isGrant: "false",
      }),
      nativeCallBackMethodForNotification({
        type: "getNotificationPermissionInfo",
        isGrant: "false",
      }),
    ]);

    const [camera, location, storage, microphone, notifications] = results.map(
      (r) =>
        r.status === "fulfilled"
          ? ((r.value as any)?.isPermissionEnabled ?? false)
          : false,
    );

    await callUpdateApi(mobileNo, csUserId, {
      camera,
      location,
      storage,
      microphone,
      notifications,
    });
  };

  const syncNativePermissions = (mobileNo: string, csUserId: string) => {
    if (!csAppSlice.NativeDeviceInfo?.deviceName) return;
    if (getMobileOperatingSystem() === "iOS") {
      syncIosPermissions(mobileNo, csUserId);
    } else {
      syncAndroidPermissions(mobileNo, csUserId);
    }
  };

  return { syncNativePermissions };
};

export default useNativePermissionSync;
